import { WatchingGuard } from 'interfaces/guards'
import {
  feedQuerySelector,
  isCurrentlyFilteredPostsLoaded,
  feedFiltersPostsLoadingSelector,
  feedPostUidsPaginatableBySelectedFilterSelector,
} from 'selectors/feedSelectors'
import { FeedFilterQuery, PostUid, FEED_ORDERING_RANK, FEED_ORDERING_BY_CREATED } from 'interfaces/feed'
import { getPostsActionCreator } from 'actions/feedActions'
import { connectOptionallyAdvanced, OptionalRendererProps, createOptionalRenderer, shallowEqual } from 'tangle-shared'
import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { StoreDispatch } from 'interfaces/component'
import { State } from 'interfaces/state'
import { CursorPaginatable } from 'interfaces/table'
import { createSelector } from 'reselect'
import FeedPostList, { FeedPostListProps, FeedPostListOwnProps } from 'components/features/socialFeed/FeedPostList/FeedPostList'

type FeedSelectedFilterWatch = {
  query: FeedFilterQuery | null
  feedPostUids: CursorPaginatable<PostUid> | null
}
const feedPostsGuard: WatchingGuard<FeedPostListOwnProps, FeedSelectedFilterWatch> = {
  name: `Feed Post List Container`,
  checker: isCurrentlyFilteredPostsLoaded,
  watcher: (state, ownProps) => ({
    query: feedQuerySelector(state, ownProps),
    feedPostUids: feedPostUidsPaginatableBySelectedFilterSelector(state, ownProps),
  }),
  loader: (dispatch, { query, feedPostUids }) => {
    if (!query) {
      return
    }

    const isEveryone = query.filter_by === 'feedgroup' && query.slug === 'everyone'
    const isCustomGroup = query.filter_by === 'peergroup' && !query.loose
    const ordering = isEveryone || isCustomGroup ? FEED_ORDERING_RANK : FEED_ORDERING_BY_CREATED
    dispatch(getPostsActionCreator.request({ query, feedPostUids, ordering }))
  },
}

type InterimStateProps = {
  nextItemIdsPaginatable: CursorPaginatable<PostUid> | null
  isLoading: boolean
  errorCode: number | null
}

// TODO: refactor this hell, embed everything to the component itself with hooks
const selectorFactory = (dispatch: StoreDispatch) => {
  let prevInterimStateProps: InterimStateProps
  let prevProps: OptionalRendererProps<FeedPostListProps>
  let prevQuery: FeedFilterQuery
  let prevOwnProps: FeedPostListOwnProps

  const interimStatePropsSelector = createSelector(
    feedFiltersPostsLoadingSelector,
    (postsLoading): InterimStateProps => ({
      nextItemIdsPaginatable: postsLoading ? postsLoading.pagination : null,
      isLoading: postsLoading ? postsLoading.isLoading : false,
      errorCode: postsLoading ? postsLoading.errorCode : null,
    })
  )

  const optionalRenderer = createOptionalRenderer<FeedPostListProps>()
  return (nextState: State, nextOwnProps: FeedPostListOwnProps): OptionalRendererProps<FeedPostListProps> => {
    const nextInterimStateProps = interimStatePropsSelector(nextState, nextOwnProps)
    const nextQuery = feedQuerySelector(nextState, nextOwnProps)
    const feedPostUids = feedPostUidsPaginatableBySelectedFilterSelector(nextState, nextOwnProps)

    const { isLoading, errorCode, nextItemIdsPaginatable } = nextInterimStateProps

    if (!nextQuery) {
      return (prevProps = optionalRenderer.fallback())
    }

    if (
      prevInterimStateProps === nextInterimStateProps
      && prevQuery === nextQuery
      && prevOwnProps && shallowEqual(prevOwnProps, nextOwnProps)
    ) {
      return prevProps
    }

    const onLoadMore = () => dispatch(getPostsActionCreator.request({ query: nextQuery, feedPostUids }))

    prevOwnProps = nextOwnProps

    if (!nextItemIdsPaginatable) {
      return optionalRenderer.render({
        itemIds: [],
        hasMore: true,
        isLoading,
        errorCode,
        onLoadMore,
        resource: nextOwnProps.resource,
        additionalBlocks: nextOwnProps.additionalBlocks,
      })
    }

    prevInterimStateProps = nextInterimStateProps
    prevQuery = nextQuery
    const { pagination, results } = nextItemIdsPaginatable
    prevProps = optionalRenderer.render({
      itemIds: results,
      hasMore: Boolean(pagination && pagination.next),
      isLoading,
      errorCode,
      onLoadMore,
      resource: nextOwnProps.resource,
      additionalBlocks: nextOwnProps.additionalBlocks,
    })

    return prevProps
  }
}

const FeedPostListContainerOptional = connectOptionallyAdvanced<
  FeedPostListProps, FeedPostListOwnProps, State
>(selectorFactory)(FeedPostList)

const FeedPostListContainer = connectShadedLoadable<
  // TODO
  {}, {}, FeedPostListOwnProps, {}
>(() => () => ({}), undefined, [feedPostsGuard])(FeedPostListContainerOptional)

export default FeedPostListContainer
